import {
  type ApolloCache,
  type ApolloClient,
  type ApolloError,
  type NormalizedCacheObject,
  useApolloClient,
} from '@apollo/client';
import type { Education } from '@seek/ca-myprofile-utils';
import { useTranslations } from '@vocab/react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { CREATE_RESUME_PAGE } from 'src/components/Router';
import { usePage } from 'src/components/Router/usePage';
import { useQuery, useToastMutation } from 'src/modules/apollo';
import { getDataFromCache } from 'src/modules/apollo/apolloCache';
import { useAppConfig } from 'src/modules/appConfig';
import { useCrossProductTransitionUrl } from 'src/modules/hooks/useCrossProductTransitionUrl';
import type { ActionName, Viewer } from 'src/types';
import {
  GetConfirmedQualificationsDocument,
  GetUnconfirmedQualificationsDocument,
} from 'src/types/graphql';
import * as Schema from 'src/types/graphql';

import { useCandidateId } from '../data';

import translations from './.vocab';
import {
  addUnconfirmedQualification,
  addUnconfirmedQualificationFailure,
  addUnconfirmedQualificationSuccess,
  rejectUnconfirmedQualification,
  rejectUnconfirmedQualificationFailure,
  rejectUnconfirmedQualificationSuccess,
  removeQualification,
  removeQualificationFailure,
  removeQualificationSuccess,
  updateQualification,
  updateQualificationFailure,
  updateQualificationSuccess,
  updateVerifiedQualification,
  updateVerifiedQualificationFailure,
  updateVerifiedQualificationSuccess,
} from './actionCreators';
import {
  generateConfirmQualificationOptimisticResponse,
  generateDeleteQualificationOptimisticResponse,
  generateRejectUnconfirmedDataOptimisticResponse,
  generateUpdateQualificationOptimisticResponse,
} from './apolloCache.optimistic';
import {
  updateCacheConfirmQualification,
  updateCacheDeleteQualification,
  updateCacheRejectQualification,
  updateCacheUpdateQualification,
  updateCacheUpdateVerifiedQualification,
} from './apolloCache.updates';
import {
  getConfirmedQualifications,
  getUnconfirmedQualifications,
} from './qualification.graphql';

export const useGetUnconfirmedQualifications = () => {
  const { language, zone, isUnifiedSite } = useAppConfig();
  const getCrossProductUrl = useCrossProductTransitionUrl('seekpass');

  const page = usePage();
  const isCreateResumePage = page === CREATE_RESUME_PAGE;
  const shouldSkip = isCreateResumePage;

  const { data, loading, error } = useQuery<
    Viewer<{ unconfirmedQualifications: Education[] }>
  >(getUnconfirmedQualifications, {
    skip: shouldSkip,
    bypassErrorPageOnFail: true,
    variables: {
      languageCode: language,
      zone,
      isUnifiedSite,
    },
  });

  const [unconfirmedQualifications, setUnconfirmedQualifications] = useState<
    Education[]
  >(data?.viewer?.unconfirmedQualifications ?? []);

  useEffect(() => {
    const transformUrls = async () => {
      const rawQualifications = data?.viewer?.unconfirmedQualifications ?? [];

      const transformedQualifications = await Promise.all(
        rawQualifications.map(async (qualification) => {
          const transformed = { ...qualification };

          if (qualification.verificationUrl) {
            transformed.verificationUrl = await getCrossProductUrl(
              qualification.verificationUrl,
            );
          }

          if (qualification.credential?.manageVerificationUrl) {
            const deleteUrl = qualification.credential.deleteVerificationUrl;
            const transformedDeleteUrl = deleteUrl
              ? await getCrossProductUrl(deleteUrl)
              : deleteUrl;

            transformed.credential = {
              ...qualification.credential,
              manageVerificationUrl: await getCrossProductUrl(
                qualification.credential.manageVerificationUrl,
              ),
              deleteVerificationUrl: transformedDeleteUrl,
            };
          }

          return transformed;
        }),
      );

      setUnconfirmedQualifications(transformedQualifications);
    };

    transformUrls();
  }, [data, getCrossProductUrl]);

  return {
    unconfirmedQualifications,
    loading,
    error,
  };
};

export const getConfirmedQualificationsFromCache = <
  T extends Record<string, any>,
>({
  cache,
  languageCode,
  zone,
}: {
  cache: ApolloCache<T>;
} & Omit<
  Schema.ConfirmQualificationMutationVariables,
  'qualification' | 'isUnifiedSite'
>) => {
  const confirmedQualificationsData = getDataFromCache({
    cache,
    query: GetConfirmedQualificationsDocument,
    variables: { languageCode, zone, isUnifiedSite: true },
  });

  return {
    confirmedQualifications:
      (confirmedQualificationsData?.viewer
        ?.confirmedQualifications as unknown as Education[]) ?? [],
  };
};

export const useGetConfirmedQualifications = () => {
  const { language, zone, isUnifiedSite } = useAppConfig();
  const getCrossProductUrl = useCrossProductTransitionUrl('seekpass');

  const { data, loading, error } = useQuery<
    Viewer<{ confirmedQualifications: Education[] }>
  >(getConfirmedQualifications, {
    variables: {
      languageCode: language,
      zone,
      isUnifiedSite,
    },
  });

  const [confirmedQualifications, setConfirmedQualifications] = useState<
    Education[]
  >(data?.viewer?.confirmedQualifications || []);

  useEffect(() => {
    const transformUrls = async () => {
      const rawQualifications = data?.viewer?.confirmedQualifications || [];

      const transformedQualifications = await Promise.all(
        rawQualifications.map(async (qualification) => {
          const transformed = { ...qualification };

          if (qualification.verificationUrl) {
            transformed.verificationUrl = await getCrossProductUrl(
              qualification.verificationUrl,
            );
          }

          if (qualification.credential?.manageVerificationUrl) {
            const deleteUrl = qualification.credential.deleteVerificationUrl;
            const transformedDeleteUrl = deleteUrl
              ? await getCrossProductUrl(deleteUrl)
              : deleteUrl;

            transformed.credential = {
              ...qualification.credential,
              manageVerificationUrl: await getCrossProductUrl(
                qualification.credential.manageVerificationUrl,
              ),
              deleteVerificationUrl: transformedDeleteUrl,
            };
          }

          return transformed;
        }),
      );

      setConfirmedQualifications(transformedQualifications);
    };

    transformUrls();
  }, [data, getCrossProductUrl]);

  return {
    confirmedQualifications,
    loading,
    error,
  };
};

export const getUnconfirmedQualificationsFromCache = ({
  apolloClient,
  languageCode,
  zone,
}: {
  apolloClient: ApolloClient<NormalizedCacheObject | Record<string, unknown>>;
} & Omit<
  Schema.ConfirmQualificationMutationVariables,
  'qualification' | 'isUnifiedSite'
>) => {
  const unconfirmedQualificationsData = getDataFromCache({
    cache: apolloClient.cache,
    query: GetUnconfirmedQualificationsDocument,
    variables: { languageCode, zone, isUnifiedSite: true },
  });

  const unconfirmedQualifications =
    (unconfirmedQualificationsData?.viewer
      ?.unconfirmedQualifications as unknown as Education[]) || [];

  return {
    unconfirmedQualifications,
  };
};

const key = 'education';

export const useUpdateQualification = (action: ActionName) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();

  const onCompleted = () => {
    dispatch(updateQualificationSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(updateQualificationFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.UpdateQualificationDocument, {
    key,
    errorActionRouteParameters: { card: 'education', mode: 'READ' },
    onMutate: ({ qualification }) => {
      dispatch(updateQualification(qualification, action));
    },
    onCompleted,
    onError,
    successToastMessage:
      action === 'ADD' ? t('Education added') : t('Education updated'),
    update: updateCacheUpdateQualification,
    optimisticResponse: generateUpdateQualificationOptimisticResponse({
      apolloClient,
      _id: candidateId || '',
    }),
  });
};

export const useDeleteQualification = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();

  const onMutate = () => {
    dispatch(removeQualification());
  };

  const onCompleted = () => {
    dispatch(removeQualificationSuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(removeQualificationFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.DeleteQualificationDocument, {
    key,
    errorActionRouteParameters: { card: 'education', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Education removed'),
    update: updateCacheDeleteQualification,
    optimisticResponse: generateDeleteQualificationOptimisticResponse({
      _id: candidateId || '',
      apolloClient,
    }),
  });
};

export const useAddUnconfirmedQualification = (action: ActionName) => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();

  const onMutate = (
    { qualification }: Schema.ConfirmQualificationMutationVariables,
    mutationMetadata?: { contentRead: boolean },
  ) => {
    dispatch(
      addUnconfirmedQualification(
        { id: qualification.id },
        action,
        mutationMetadata?.contentRead,
      ),
    );
  };

  const onCompleted = () => {
    dispatch(addUnconfirmedQualificationSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(addUnconfirmedQualificationFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.ConfirmQualificationDocument, {
    key,
    errorActionRouteParameters: { card: 'education', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Education added'),
    update: updateCacheConfirmQualification,
    optimisticResponse: generateConfirmQualificationOptimisticResponse({
      apolloClient,
      _id: candidateId || '',
    }),
  });
};

export const useUpdateVerifiedQualification = () => {
  const { t } = useTranslations(translations);
  const dispatch = useDispatch();

  const onMutate = () => {
    dispatch(updateVerifiedQualification());
  };
  const onCompleted = () => {
    dispatch(updateVerifiedQualificationSuccess());
  };
  const onError = (error: ApolloError) => {
    dispatch(updateVerifiedQualificationFailure(error));
  };

  return useToastMutation(Schema.UpdateVerifiedQualificationDocument, {
    key,
    errorActionRouteParameters: { card: 'education', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    successToastMessage: t('Education updated'),
    update: updateCacheUpdateVerifiedQualification,
  });
};

export const useRejectUnconfirmedQualification = () => {
  const dispatch = useDispatch();
  const { candidateId } = useCandidateId();

  const onMutate = ({
    qualification,
  }: Schema.RejectQualificationMutationVariables) => {
    dispatch(rejectUnconfirmedQualification({ id: qualification.id }));
  };

  const onCompleted = () => {
    dispatch(rejectUnconfirmedQualificationSuccess());
  };

  const onError = (error: ApolloError) => {
    dispatch(rejectUnconfirmedQualificationFailure(error));
  };

  const apolloClient = useApolloClient();

  return useToastMutation(Schema.RejectQualificationDocument, {
    key,
    errorActionRouteParameters: { card: 'education', mode: 'READ' },
    onMutate,
    onCompleted,
    onError,
    update: updateCacheRejectQualification,
    optimisticResponse: generateRejectUnconfirmedDataOptimisticResponse({
      _id: candidateId || '',
      apolloClient,
    }),
  });
};
